import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Edit, ExternalLink } from "lucide-react";
import { db } from "../../firebase/config";
import JoditEditor from "jodit-react";

const styles = `
  .job-management-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
    padding: 24px;
  }

  .container-inner {
    max-width: 1200px;
    margin: 0 auto;
  }

  .page-header {
    margin-bottom: 32px;
  }

  .page-title {
    color: #0D47A1;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .form-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 24px;
    margin-bottom: 32px;
  }

  .form-title {
    color: #0D47A1;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 24px;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 20px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .form-group.full-width {
    grid-column: span 2;
  }

  .form-label {
    color: #0D47A1;
    font-weight: 600;
    font-size: 0.875rem;
    margin-bottom: 8px;
  }

  .form-label.required::after {
    content: '*';
    color: #dc3545;
    margin-left: 4px;
  }

  .form-input {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.875rem;
    transition: border-color 0.2s;
  }

  .form-input:focus {
    outline: none;
    border-color: #0D47A1;
  }

  .form-textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.875rem;
    min-height: 120px;
    font-family: inherit;
    resize: vertical;
    transition: border-color 0.2s;
  }

  .form-textarea:focus {
    outline: none;
    border-color: #0D47A1;
  }

  .must-haves-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .must-have-item {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .must-have-input {
    flex: 1;
  }

  .add-btn-small {
    padding: 8px 16px;
    background: #0D47A1;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .add-btn-small:hover {
    background: #0a3d8f;
  }

  .remove-btn {
    padding: 8px;
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .remove-btn:hover {
    background: #c82333;
  }

  .submit-btn {
    width: 100%;
    padding: 14px;
    background: #0D47A1;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 20px;
  }

  .submit-btn:hover {
    background: #0a3d8f;
    transform: scale(1.01);
  }

  .jobs-list-title {
    color: #0D47A1;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 20px;
  }

  .job-list-card {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 16px;
    margin-bottom: 16px;
    transition: all 0.2s;
  }

  .job-list-card:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .job-list-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .job-list-info {
    flex: 1;
  }

  .job-list-id {
    color: #0D47A1;
    font-weight: 700;
    font-size: 1rem;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  .job-list-id:hover {
    text-decoration: underline;
  }

  .job-list-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  .job-list-details {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    font-size: 0.875rem;
    color: #666;
  }

  .job-list-detail {
    display: flex;
    gap: 4px;
  }

  .job-list-label {
    font-weight: 600;
    color: #0D47A1;
  }

  .job-actions {
    display: flex;
    gap: 8px;
  }

  .action-btn {
    padding: 6px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  .action-btn:hover {
    opacity: 0.8;
  }

  .edit-btn {
    background: #ffc107;
    color: white;
  }

  .delete-btn {
    background: #dc3545;
    color: white;
  }

  .editor-container {
    border: 1px solid #ddd;
    border-radius: 6px;
    min-height: 200px;
    padding: 12px;
    background: white;
  }

  .editor-toolbar {
    display: flex;
    gap: 4px;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ddd;
  }

  .toolbar-btn {
    padding: 4px 8px;
    border: 1px solid #ddd;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.2s;
  }

  .toolbar-btn:hover {
    background: #f0f0f0;
  }

  .editor-content {
    min-height: 150px;
    outline: none;
    font-size: 0.875rem;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    .form-grid {
      grid-template-columns: 1fr;
    }
    
    .form-group.full-width {
      grid-column: span 1;
    }
    
    .job-list-details {
      grid-template-columns: 1fr;
    }
  }
`;

const SimpleEditor = ({ value, onChange }) => {
  const editorRef = React.useRef(null);

  const formatText = (command) => {
    document.execCommand(command, false, null);
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  React.useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || "";
    }
  }, []);

  return (
    <div className="editor-container">
      <div className="editor-toolbar">
        <button
          className="toolbar-btn"
          onClick={() => formatText("bold")}
          type="button"
        >
          <strong>B</strong>
        </button>
        <button
          className="toolbar-btn"
          onClick={() => formatText("italic")}
          type="button"
        >
          <em>I</em>
        </button>
        <button
          className="toolbar-btn"
          onClick={() => formatText("underline")}
          type="button"
        >
          <u>U</u>
        </button>
        <button
          className="toolbar-btn"
          onClick={() => formatText("insertUnorderedList")}
          type="button"
        >
          • List
        </button>
      </div>
      <div
        ref={editorRef}
        className="editor-content"
        contentEditable
        onInput={handleInput}
      />
    </div>
  );
};

const JobPostingManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({
    jobId: "",
    title: "",
    client: "",
    startDate: "",
    endDate: "",
    extensionDays: "",
    maxExtensions: "",
    location: "222 Jarvis Street, Toronto",
    closingDate: "",
    closingTime: "",
    mustHaves: "",
    description: "",
    closingHour: "00",
    closingPeriod: "AM",
  });

  const getjobs = async () => {
    await db
      .collection("opsJobs")
      .get()
      .then((res) => {
        if (res.empty) {
          return;
        }
        res.forEach((ele) => {
          console.log(ele.data());
          setJobs((arr) => [...arr, ele.data()]);
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };
  // console.log(blogs);
  useEffect(() => {
    getjobs();
  }, []);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMustHaveChange = (index, value) => {
    const newMustHaves = [...formData.mustHaves];
    newMustHaves[index] = value;
    setFormData((prev) => ({
      ...prev,
      mustHaves: newMustHaves,
    }));
  };

  const addMustHave = () => {
    setFormData((prev) => ({
      ...prev,
      mustHaves: [...prev.mustHaves, ""],
    }));
  };

  const removeMustHave = (index) => {
    const newMustHaves = formData.mustHaves.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      mustHaves: newMustHaves.length ? newMustHaves : [""],
    }));
  };

  const handleSubmit = () => {
    console.log(formData);

    if (
      !formData.jobId ||
      !formData.title ||
      !formData.client ||
      !formData.startDate ||
      !formData.endDate ||
      !formData.location ||
      !formData.closingDate 
    
    ) {
      alert("Please fill all required fields");
      return;
    }
    console.log("Submitting job:", formData);
    const newJob = {
      id: Date.now(),
      ...formData,
      mustHaves: formData.mustHaves,
      closingDateTime: `${formData.closingDate}, ${formData.closingHour}:00${formData.closingPeriod.toLocaleLowerCase()}`,
    };

    db.collection("opsJobs")
      .doc(newJob.title + "_" + newJob.jobId)
      .set(newJob)
      .then(() => {
        setJobs((prev) => [newJob, ...prev]);

        setFormData({
          jobId: "",
          title: "",
          client: "",
          startDate: "",
          endDate: "",
          extensionDays: "",
          maxExtensions: "",
          location: "",
          closingDate: "",
          closingTime: "",
          mustHaves: "",
          description: "",
          closingHour: formData.closingHour,
          closingPeriod: formData.closingPeriod,  
        });
      });
  };

  const deleteJob = (id) => {
    setJobs((prev) => prev.filter((job) => job.id !== id));
  };

  const viewJobDetails = (jobId) => {
    alert(`Navigating to detailed page for ${jobId}`);
  };

  const editor = useRef(null);
  const config = {
    readonly: false,
    toolbarAdaptive: false,
    height: 300,
    buttons: [
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "ul",
      "ol",
      "outdent",
      "indent",
      "|",
      "font",
      "fontsize",
      "paragraph",
      "brush",
      "align",
      "|",
      "link",
      "image",
      "video",
      "table",
      "|",
      "undo",
      "redo",
      "hr",
      "eraser",
      "fullsize",
      "source",
    ],
  };

  return (
    <>
      <style>{styles}</style>
      <div className="job-management-container">
        <div className="container-inner">
          <div className="page-header">
            <h1 className="page-title">Job Posting Management</h1>
          </div>

          <div className="form-card">
            <h2 className="form-title">Create New Job Posting</h2>
            <div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">
                    Job ID (RQ Number)
                  </label>
                  <input
                    type="text"
                    name="jobId"
                    className="form-input"
                    value={formData.jobId}
                    onChange={handleInputChange}
                    placeholder="e.g., RQ09988"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Client</label>
                  <input
                    type="text"
                    name="client"
                    className="form-input"
                    value={formData.client}
                    onChange={handleInputChange}
                    placeholder="e.g., CAC"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label required">Title</label>
                  <input
                    type="text"
                    name="title"
                    className="form-input"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Sr. Business Analyst"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    className="form-input"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    className="form-input"
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Extension # of Days</label>
                  <input
                    type="number"
                    name="extensionDays"
                    className="form-input"
                    value={formData.extensionDays}
                    onChange={handleInputChange}
                    placeholder="e.g., 125"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Max Extensions</label>
                  <input
                    type="number"
                    name="maxExtensions"
                    className="form-input"
                    value={formData.maxExtensions}
                    onChange={handleInputChange}
                    placeholder="e.g., 2"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label required">Onsite Location</label>
                  <input
                    type="text"
                    name="location"
                    className="form-input"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="e.g., 222 Jarvis Street, Toronto"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Closing Date</label>
                  <input
                    type="date"
                    name="closingDate"
                    className="form-input"
                    value={formData.closingDate}
                    onChange={handleInputChange}
                  />
                </div>

                {/* <div className="form-group">
                  <label className="form-label required">Closing Time</label>
                  <input
                    type="time"
                    name="closingTime"
                    className="form-input"
                    value={formData.closingTime}
                    onChange={handleInputChange}
                  />
                </div> */}
                <div className="form-group">
  <label className="form-label required">Closing Time</label>
  <div style={{ display: "flex", gap: "8px" }}>
    <select
      name="closingHour"
      className="form-input"
      value={formData.closingHour}
      onChange={handleInputChange}
    >
      {[...Array(12)].map((_, i) => (
        <option key={i + 1} value={i + 1}>
          {String(i + 1).padStart(2, "0")}
        </option>
      ))}
    </select>

    <select
      name="closingPeriod"
      className="form-input"
      value={formData.closingPeriod}
      onChange={handleInputChange}
    >
      <option value="AM">AM</option>
      <option value="PM">PM</option>
    </select>
  </div>
</div>


                {/* <div className="form-group full-width">
                  <label className="form-label required">
                    Must Have Skills
                  </label>
                  <div className="must-haves-container">
                    {formData.mustHaves.map((item, index) => (
                      <div key={index} className="must-have-item">
                        <input
                          type="text"
                          className="form-input must-have-input"
                          value={item}
                          onChange={(e) =>
                            handleMustHaveChange(index, e.target.value)
                          }
                          placeholder="Enter required skill or qualification"
                        />
                        {formData.mustHaves.length > 1 && (
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeMustHave(index)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="add-btn-small"
                      onClick={addMustHave}
                    >
                      <Plus
                        size={16}
                        style={{
                          display: "inline",
                          verticalAlign: "middle",
                          marginRight: "4px",
                        }}
                      />
                      Add More
                    </button>
                  </div>
                </div> */}
                {/* <div className="form-group full-width">
                  <label className="form-label">Must Have Skills</label>
                  <SimpleEditor
                    value={formData.mustHaves}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, mustHaves: value }))
                    }
                  />
                </div> */}
                <div className="form-group full-width">
                  <label className="form-label">Must Have Skills</label>
                  <JoditEditor
                    ref={editor}
                    value={formData.mustHaves}
                   // config={config}
                    tabIndex={1}
                    onBlur={(newContent) =>
                      setFormData((prev) => ({
                        ...prev,
                        mustHaves: newContent,
                      }))
                    }
                    onChange={() => {}}
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Job Description</label>
                  <JoditEditor
                    //ref={editor}
                    value={formData.description}
                  //  config={config}
                    tabIndex={1}
                    onBlur={(newContent) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: newContent,
                      }))
                    }
                    onChange={() => {}} // we can leave this empty for performance
                  />
                </div>
                {/* <div className="form-group full-width">
                  <label className="form-label">Job Description</label>
                  <SimpleEditor
                    value={formData.description}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, description: value }))
                    }
                  />
                </div> */}
              </div>

              <button
                type="button"
                className="submit-btn"
                onClick={handleSubmit}
              >
                Submit Job Posting
              </button>
            </div>
          </div>

          {jobs.length > 0 && (
            <div>
              <h2 className="jobs-list-title">Posted Jobs ({jobs.length})</h2>
              {jobs.map((job) => (
                <div key={job.id} className="job-list-card">
                  <div className="job-list-header">
                    <div className="job-list-info">
                      <div
                        className="job-list-id"
                        onClick={() => viewJobDetails(job.jobId)}
                      >
                        {job.jobId}
                        <ExternalLink size={14} />
                      </div>
                      <div className="job-list-title">{job.title}</div>
                      <div className="job-list-details">
                        <div className="job-list-detail">
                          <span className="job-list-label">Client:</span>
                          <span>{job.client}</span>
                        </div>
                        <div className="job-list-detail">
                          <span className="job-list-label">Location:</span>
                          <span>{job.location}</span>
                        </div>
                        <div className="job-list-detail">
                          <span className="job-list-label">Start:</span>
                          <span>{job.startDate}</span>
                        </div>
                        <div className="job-list-detail">
                          <span className="job-list-label">Closing:</span>
                          <span>{job.closingDateTime}</span>
                        </div>
                      </div>
                    </div>
                    <div className="job-actions">
                      {/* <button
                        className="action-btn delete-btn"
                        onClick={() => deleteJob(job.id)}
                      >
                        <Trash2 size={16} />
                      </button> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JobPostingManagement;
